import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import PageHero from '../../components/feature/PageHero';
import { useEffect } from 'react';
import SEOHead from '../../components/feature/SEOHead';

const strategies = [
  {
    icon: 'ri-search-eye-line',
    title: 'Diagnóstico Completo',
    description: 'Análise detalhada do perfil de risco, histórico de sinistros e necessidades específicas da sua empresa ou patrimônio pessoal.',
    color: 'orange'
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Revisão Técnica',
    description: 'Avaliação minuciosa de cláusulas, coberturas, exclusões e condições contratuais para identificar gaps de proteção.',
    color: 'green'
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Estruturação de Coberturas',
    description: 'Dimensionamento correto de capitais segurados, franquias e coberturas adicionais alinhadas ao risco real.',
    color: 'orange'
  },
  {
    icon: 'ri-team-line',
    title: 'Negociação Estratégica',
    description: 'Relacionamento direto com seguradoras e corretores para obter as melhores condições técnicas e comerciais.',
    color: 'green'
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Otimização de Custos',
    description: 'Redução inteligente de prêmios sem comprometer a qualidade da proteção através de análise comparativa.',
    color: 'orange'
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Acompanhamento Contínuo',
    description: 'Monitoramento ativo durante toda a vigência com revisões periódicas e suporte em processos de sinistros.',
    color: 'green'
  }
];

const benefits = [
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Economia Real',
    description: 'Redução de até 35% nos custos com seguros através de negociação técnica e estruturação adequada de coberturas.'
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Proteção Efetiva',
    description: 'Coberturas dimensionadas corretamente para o risco real, evitando surpresas desagradáveis em sinistros.'
  },
  {
    icon: 'ri-time-line',
    title: 'Agilidade',
    description: 'Processos otimizados e relacionamento direto com seguradoras para resolução rápida de demandas.'
  },
  {
    icon: 'ri-user-star-line',
    title: 'Expertise',
    description: 'Mais de 25 anos de experiência em gestão de riscos e contratação profissional de seguros corporativos.'
  }
];

export default function EstrategiaPage() {
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Estratégia de Proteção Empresarial",
    "description": "Nossa estratégia de consultoria em seguros empresariais: análise de riscos, otimização de coberturas e gestão estratégica de proteção patrimonial.",
    "url": "https://ibxbrasil.com.br/estrategia"
  };

  return (
    <>
      <SEOHead
        title="Estratégia de Proteção Empresarial | IBX Brasil"
        description="Descubra nossa estratégia de consultoria em seguros: análise de riscos corporativos, otimização de coberturas e gestão estratégica de proteção patrimonial."
        canonical="https://ibxbrasil.com.br/estrategia"
        ogImage="https://readdy.ai/api/search-image?query=strategic%20business%20insurance%20planning%20with%20risk%20analysis%20charts%20and%20corporate%20protection%20strategy%20documents%20on%20modern%20desk&width=1200&height=630&seq=ibx-estrategia-og&orientation=landscape"
        schema={webPageSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <PageHero
          badge="ESTRATÉGIA IBX"
          title="Metodologia Estratégica para Contratação Profissional"
          highlightedWord="Profissional"
          highlightColor="orange"
          subtitle="Nossa abordagem combina análise técnica detalhada, negociação especializada e acompanhamento contínuo para garantir proteção patrimonial com economia inteligente."
          benefits={[
            'Diagnóstico completo de riscos e necessidades',
            'Revisão técnica de cláusulas e condições',
            'Negociação estratégica com seguradoras',
            'Otimização de custos sem perder proteção'
          ]}
          ctaText="Solicitar Análise Estratégica"
          ctaLink="/#contato"
          ctaSecondary={{
            text: 'Falar com Especialista',
            link: 'https://wa.me/5577988080505'
          }}
          backgroundImage="https://readdy.ai/api/search-image?query=strategic%20business%20planning%20meeting%20with%20insurance%20professionals%20analyzing%20risk%20management%20charts%20and%20financial%20data%20on%20large%20screens%20in%20modern%20corporate%20boardroom%20with%20natural%20lighting%20representing%20expertise%20and%20professional%20consulting%20services&width=1920&height=800&seq=estrategia-hero-001&orientation=landscape"
          overlayOpacity="medium"
        />

        {/* Strategy Steps */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Nossa Metodologia em 6 Etapas
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Processo estruturado para garantir a melhor contratação de seguros com economia e proteção real
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {strategies.map((strategy, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-lg p-8 hover:border-orange-500 hover:shadow-xl transition-all duration-300"
                >
                  <div className={`w-16 h-16 bg-${strategy.color}-100 rounded-lg flex items-center justify-center mb-6`}>
                    <i className={`${strategy.icon} text-3xl text-${strategy.color}-600`}></i>
                  </div>
                  
                  <div className="flex items-start gap-3 mb-4">
                    <span className="text-4xl font-bold text-gray-200">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <h3 className="text-xl font-bold text-gray-900 mt-1">
                      {strategy.title}
                    </h3>
                  </div>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {strategy.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Benefícios da Abordagem Estratégica
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Resultados concretos através de gestão profissional de seguros
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${benefit.icon} text-4xl text-orange-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Contratação Tradicional vs. Estratégica
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Entenda a diferença entre contratar seguros de forma convencional e profissionalizada
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Traditional */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                    <i className="ri-close-line text-2xl text-gray-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Contratação Tradicional
                  </h3>
                </div>
                
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-line text-xl text-gray-400 mt-0.5"></i>
                    <span className="text-gray-600">Foco apenas no preço mais baixo</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-line text-xl text-gray-400 mt-0.5"></i>
                    <span className="text-gray-600">Pouca ou nenhuma análise de cláusulas</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-line text-xl text-gray-400 mt-0.5"></i>
                    <span className="text-gray-600">Coberturas desalinhadas ao risco real</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-line text-xl text-gray-400 mt-0.5"></i>
                    <span className="text-gray-600">Negociação limitada ou inexistente</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-line text-xl text-gray-400 mt-0.5"></i>
                    <span className="text-gray-600">Descoberta de falhas apenas no sinistro</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-line text-xl text-gray-400 mt-0.5"></i>
                    <span className="text-gray-600">Sem acompanhamento durante a vigência</span>
                  </li>
                </ul>
              </div>

              {/* Strategic */}
              <div className="bg-gradient-to-br from-orange-50 to-green-50 border-2 border-orange-500 rounded-lg p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Contratação Estratégica IBX
                  </h3>
                </div>
                
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-orange-600 mt-0.5"></i>
                    <span className="text-gray-900 font-medium">Diagnóstico completo do perfil de risco</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-orange-600 mt-0.5"></i>
                    <span className="text-gray-900 font-medium">Revisão técnica detalhada de cláusulas</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-orange-600 mt-0.5"></i>
                    <span className="text-gray-900 font-medium">Estruturação correta de coberturas</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-orange-600 mt-0.5"></i>
                    <span className="text-gray-900 font-medium">Negociação estratégica com seguradoras</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-orange-600 mt-0.5"></i>
                    <span className="text-gray-900 font-medium">Otimização de custos com proteção real</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-orange-600 mt-0.5"></i>
                    <span className="text-gray-900 font-medium">Acompanhamento contínuo e suporte em sinistros</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-petrol-700 to-petrol-800">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Pronto para Adotar uma Estratégia Profissional?
            </h2>
            <p className="text-xl text-petrol-100 mb-8">
              Solicite uma análise estratégica gratuita e descubra como nossa metodologia pode proteger seu patrimônio com economia inteligente
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://wa.me/5577988080505"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-white text-petrol-700 rounded-lg font-semibold hover:bg-petrol-50 transition-colors duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                <span>Falar com Especialista</span>
              </a>
              <a
                href="/#contato"
                className="px-8 py-4 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Solicitar Análise Estratégica
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}